import {
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SpeedDial,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';
import onboardingSlideService from '../../services/onboardingSlideService';
import tenantService from '../tenants/tenantService';

export enum OnboardingMode {
  CityDefault = 0,
  Custom = 1,
  Off = 2,
}

const ONBOARDING_MODE_OPTIONS = [
  {
    value: OnboardingMode.CityDefault,
    label: 'Alapértelmezett városi intro',
  },
  {
    value: OnboardingMode.Custom,
    label: 'Egyedi slideok',
  },
  {
    value: OnboardingMode.Off,
    label: 'Kikapcsolva',
  },
];

const resolveImageUrl = (image: any): string | null => {
  if (!image) return null;
  return (
    image.thumbnailUrl ||
    image.ThumbnailUrl ||
    image.url1_1 ||
    image.Url1_1 ||
    image.originalUrl ||
    image.OriginalUrl ||
    null
  );
};

const OnboardingSlidesPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [onboardingMode, setOnboardingMode] = useState<OnboardingMode>(
    OnboardingMode.CityDefault
  );
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { checkPermission, canPlatform } = useCheckPermission();
  const activeTenantId = useSelector(
    (state: any) => state.user.userInfo?.activeTenantId
  );
  const canEditMode = canPlatform(['ManageTenants']);
  const isCustom = onboardingMode === OnboardingMode.Custom;

  const loadSlides = () => {
    dispatch({ type: 'SHOW_QUERY' });
    onboardingSlideService
      .list()
      .then((response) => {
        if (!response.hasError) {
          const sorted = [...(response.records ?? [])].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0)
          );
          setRows(sorted);
        } else {
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const loadSettings = () => {
    if (!activeTenantId) return;
    dispatch({ type: 'SHOW_QUERY' });
    tenantService
      .getSettings(activeTenantId)
      .then((response) => {
        if (!response.hasError && response.result) {
          setSettings(response.result);
          const mode =
            response.result.onboardingMode ??
            response.result.OnboardingMode ??
            OnboardingMode.CityDefault;
          setOnboardingMode(Number(mode) as OnboardingMode);
        } else if (response.hasError) {
          enqueueSnackbar(
            (response.errorMessages ?? ['Beállítások betöltése sikertelen']).join(
              ','
            ),
            { variant: 'error' }
          );
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  useEffect(() => {
    loadSettings();
  }, [activeTenantId]);

  useEffect(() => {
    if (isCustom) {
      loadSlides();
    }
  }, [isCustom]);

  const saveMode = () => {
    if (!activeTenantId) {
      enqueueSnackbar('Nincs aktív bérlő kiválasztva.', { variant: 'warning' });
      return;
    }
    if (!settings) {
      enqueueSnackbar('A bérlő beállításai még nem töltődtek be.', {
        variant: 'warning',
      });
      return;
    }

    const payload = {
      ...settings,
      onboardingMode,
      OnboardingMode: onboardingMode,
    };

    dispatch({ type: 'SHOW_SAVE' });
    tenantService
      .updateSettings(activeTenantId, payload)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Onboarding mód mentve', { variant: 'success' });
          if (response.result) {
            setSettings(response.result);
            const mode =
              response.result.onboardingMode ??
              response.result.OnboardingMode ??
              onboardingMode;
            setOnboardingMode(Number(mode) as OnboardingMode);
          }
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const getActions = (params: GridRowParams) => {
    const actions: any[] = [];
    const readonly = !checkPermission(['EditOnboardingSlide']);

    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="Megtekintés">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Szerkesztés">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'Megtekintés' : 'Szerkesztés'}
        onClick={() => navigate(`/onboardingSlides/edit/${params.row.id}`)}
      />
    );

    if (checkPermission(['DeleteOnboardingSlide'])) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Törlés">
              <DeleteIcon />
            </Tooltip>
          }
          label="Törlés"
          onClick={() =>
            setParams({
              open: true,
              name: 'Biztosan törölni szeretné ezt az elemet?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: GridColDef[] = [
    {
      field: 'image',
      headerName: 'Kép',
      flex: 80,
      sortable: false,
      renderCell: (params: any) => {
        const url = resolveImageUrl(params.row.image);
        if (!url) {
          return (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Box
            component="img"
            src={url}
            alt={params.row.title || ''}
            sx={{
              width: 48,
              height: 48,
              objectFit: 'cover',
              borderRadius: 1,
              my: 0.5,
            }}
          />
        );
      },
    },
    { field: 'order', headerName: 'Sorrend', width: 90 },
    { field: 'title', headerName: 'Cím', flex: 1.2, minWidth: 160 },
    {
      field: 'body',
      headerName: 'Szöveg',
      flex: 2,
      minWidth: 200,
      valueGetter: (_value: any, row: any) => row.body || '',
      renderCell: (params: any) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'ctaLabel',
      headerName: 'Gomb',
      width: 120,
      valueGetter: (_value: any, row: any) => row.ctaLabel || '',
      renderCell: (params: any) => params.value || '—',
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      align: 'right',
      getActions: getActions,
    } as any,
  ];

  const handleDelete = (id: number) => {
    onboardingSlideService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Sikeres törlés!', { variant: 'success' });
        setRows((prev) => prev.filter((row) => row.id !== id));
      }
    });
  };

  if (!activeTenantId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Nincs aktív bérlő kiválasztva.</Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <Grid container sx={{ p: 3 }} spacing={2}>
        <Grid size={12}>
          <h2>Bevezető slideok</h2>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <FormControl fullWidth>
            <InputLabel id="onboarding-mode-label">Onboarding mód</InputLabel>
            <Select
              labelId="onboarding-mode-label"
              label="Onboarding mód"
              value={onboardingMode}
              onChange={(e) =>
                setOnboardingMode(Number(e.target.value) as OnboardingMode)
              }
            >
              {ONBOARDING_MODE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{ xs: 12, md: 4 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={saveMode}
            disabled={!canEditMode || !settings}
          >
            Mód mentése
          </Button>
        </Grid>

        {isCustom && (
          <>
            {checkPermission(['CreateOnboardingSlide']) && (
              <Grid
                container
                size={12}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Typography variant="subtitle1">
                  Slideok ({rows.length})
                </Typography>
                <Grid sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Tooltip title="Slide létrehozása">
                    <IconButton
                      component={RouterLink}
                      to="/onboardingSlides/create"
                      color="primary"
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            )}

            <Grid size={12}>
              <DataList rows={rows} columns={columns} localStorageKey="onboardingSlides" />
            </Grid>

            <ConfirmDeleteDialog />
            {checkPermission(['CreateOnboardingSlide']) && (
              <SpeedDial
                onClick={() => navigate('/onboardingSlides/create')}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  position: 'fixed',
                  bottom: 16,
                  right: 16,
                }}
                icon={<AddIcon />}
                ariaLabel="Hozzáadás"
              />
            )}
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default OnboardingSlidesPage;
